"use client";

import React from "react";
import { motion } from "framer-motion";
import { Search, Code as Code2, MapPin, Star, FileCode2, ChartLine as LineChart, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processSteps = [{
  number: "01",
  title: "Comprehensive SEO Audit",
  description: "I evaluate your website's technical foundation, content relevance, backlink profile, and competitive positioning to reveal real growth roadblocks.",
  detail: "Technical health & crawl efficiency",
  iconName: "Search"
}, {
  number: "02",
  title: "On-Page & Technical Optimization",
  description: "I resolve underlying code issues, enhance internal linking structure, and align your target pages with high-intent search queries.",
  detail: "Code refinement & site structure",
  iconName: "Code2"
}, {
  number: "03",
  title: "Citations & Local Alignment",
  description: "I build consistent business citations and optimize geographic authority signals to dominate local and regional search results.",
  detail: "Geographic signals & authority",
  iconName: "MapPin"
}, {
  number: "04",
  title: "Review & Reputation Strategy",
  description: "I help structure sustainable feedback loops and trust indicators that boost search conversions and brand trust.",
  detail: "Trust signals & conversion boost",
  iconName: "Star"
}, {
  number: "05",
  title: "Schema Markup & Entity Building",
  description: "I implement structured schema code so search engines and AI engines clearly understand your business entities and services.",
  detail: "AI search & Knowledge Graph",
  iconName: "FileCode2"
}, {
  number: "06",
  title: "Transparent Reporting & Growth",
  description: "You receive clear, direct monthly reporting focusing on organic visibility, qualified leads, and measurable revenue impact.",
  detail: "Direct updates & KPI tracking",
  iconName: "LineChart"
}];
export default function Methodology() {
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 24
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const getStepIcon = (iconName: string) => {
    switch (iconName) {
      case "Search":
        return <Search className="size-5 text-primary" />;
      case "Code2":
        return <Code2 className="size-5 text-primary" />;
      case "MapPin":
        return <MapPin className="size-5 text-primary" />;
      case "Star":
        return <Star className="size-5 text-primary" />;
      case "FileCode2":
        return <FileCode2 className="size-5 text-primary" />;
      case "LineChart":
        return <LineChart className="size-5 text-primary" />;
      default:
        return <Search className="size-5 text-primary" />;
    }
  };
  return <section id="methodologyflow" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="absolute top-1/4 left-10 size-72 bg-secondary/40 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-1/4 right-10 size-80 bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
            Tailored SEO Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-6">
            A Proven Methodology Built for Sustainable Search Growth
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            You work directly with me—no account managers, no generic agency templates, and no guesswork. My structured process delivers clean technical foundations, strategic authority, and measurable revenue growth.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-8 right-8 h-0.5 bg-gradient-to-r from-secondary via-accent/30 to-secondary -translate-y-12 -z-10" />

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {processSteps.map((step, i) => <motion.div key={step.number} variants={itemVariants} className="h-full">
                <Card data-index={i} className="bg-card text-card-foreground border-border/80 shadow-md hover:shadow-lg transition-[box-shadow,border-color] duration-300 rounded-2xl relative h-full flex flex-col justify-between overflow-hidden group hover:border-primary/40">
                  <div className="h-1.5 w-full bg-muted group-hover:bg-primary transition-colors duration-300" />
                  
                  <CardHeader className="pt-6 pb-2 px-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-xs font-bold px-3 py-1 rounded-full bg-primary text-primary-foreground shadow-xs">
                        STEP {step.number}
                      </span>
                      
                      <div className="size-10 rounded-xl bg-secondary flex items-center justify-center border border-border/50">
                        {getStepIcon(step.iconName)}
                      </div>
                    </div>

                    <CardTitle className="text-xl font-bold font-serif text-foreground group-hover:text-primary transition-colors duration-300">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pb-6 pt-2 flex-1 flex flex-col justify-between">
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      {step.description}
                    </p>

                    <div className="pt-4 border-t border-border/60 flex items-center gap-2 text-xs font-medium text-foreground/80 mt-auto">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>{step.detail}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        <div className="mt-16 md:mt-20 text-center bg-card border border-border rounded-2xl p-8 md:p-12 shadow-sm max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-left max-w-xl">
            <h3 className="text-xl md:text-2xl font-bold font-serif text-foreground mb-2">
              Ready for a Tailored SEO Strategy?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              Get direct expert access and a clear road map customized specifically for your business goals.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Button asChild size="lg" className="rounded-xl shadow-sm">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-xl">
              <Link to="/CaseStudiesPage">
                View Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}